import React, { useCallback, useState } from 'react'
import { ConfirmationModal, IConfirmationModalProps } from 'transfix-ui/components/Modals'
import { useGlobalToast } from 'transfix-ui/components/Toast'
import { useSavedLanes } from '../hooks/useSavedLanes'

const SUCCESS_MESSAGE = 'Lane info deleted sucessfully'
const ERROR_MESSAGE = 'Server error. Contact tech support for assistance.'

interface ISavedLanesDeleteModalProps extends Pick<IConfirmationModalProps, 'onClose'> {
  carrierApplicationId: string
  savedLaneId: number
  onSuccess: () => void
}

export const SavedLanesDeleteModal = ({
  carrierApplicationId,
  onClose,
  onSuccess,
  savedLaneId,
}: ISavedLanesDeleteModalProps) => {
  const { openToast } = useGlobalToast()
  const [loading, setLoading] = useState<boolean>(false)

  // Mutations
  const { deleteSavedLane } = useSavedLanes({ carrierApplicationId, savedLaneId })

  const toggleLoading = useCallback(() => {
    setLoading(loading => !loading)
  }, [])

  const showErrorToast = useCallback(() => {
    openToast({ toastMessage: ERROR_MESSAGE, toastType: 'alert' })
  }, [openToast])

  const showSuccessToast = useCallback(() => {
    openToast({ toastMessage: SUCCESS_MESSAGE })
  }, [openToast])

  const onDeleteConfirmHandler = useCallback(async () => {
    try {
      toggleLoading()
      const response = await deleteSavedLane(savedLaneId)
      if (response) {
        showSuccessToast()
        onSuccess()
      } else {
        showErrorToast()
      }
    } catch {
      showErrorToast()
    } finally {
      toggleLoading()
    }
  }, [deleteSavedLane, onSuccess, savedLaneId, showErrorToast, showSuccessToast, toggleLoading])

  return (
    <ConfirmationModal
      size={4}
      onClose={onClose}
      actions={[
        {
          label: 'Cancel',
          type: 'dismiss',
          action: onClose,
          disabled: loading,
        },
        {
          label: 'Delete Saved Lane',
          type: 'confirm',
          action: onDeleteConfirmHandler,
          disabled: loading,
          dataTest: 'saved-lane-confirm-delete-button',
        },
      ]}
      title='Delete Saved Lane'
      open>
      Are you sure you would like to delete this saved lane? By deleting the recipient will no
      longer receive personalized recommendations based on this lane.
    </ConfirmationModal>
  )
}
